// src/pages/CustomerAccounts/CustomerAccounts.jsx
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Grid, CardActionArea, CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExchangeAlt,
  faTrophy,
  faBalanceScale,
  faGamepad,
  faCoins,
  faMoneyBillTransfer,
  faMagnifyingGlass,
  faMobile,
  faUsers,
  faUserGear,
  faAddressCard,
  faTrash,
  faQrcode // [CASH-MODE]
} from '@fortawesome/free-solid-svg-icons';
import { usePermissions } from '../../context/PermissionContext';
import BackButton from '../../components/BackButton';

// Define the Customer Accounts options
const customerAccountsOptions = [
  {
    title: 'Customers Synopsis',
    icon: faAddressCard,
    path: '/customer-accounts/synopsis',
    perm: 'customers.synopsis',
    description: 'View customer profile summary, transactions, game cards, referrals, smart points, withdrawals, and welfare history'
  },
  {
    title: 'All Customers',
    icon: faUsers,
    path: '/customer-accounts/all-customers',
    perm: 'customers.all',
    description: 'View and export complete customer records'
  },
  {
    title: 'Customers Logins Manage',
    icon: faUserGear,
    path: '/customer-accounts/manage-logins',
    perm: 'customers.logins',
    description: 'Edit, block, or unblock customer accounts with reason'
  },
  {
    title: 'Account Deletion Requests',
    icon: faTrash,
    path: '/account-deletion-management',
    perm: 'customers.deletion',
    description: 'View and process account deletion requests from customers, shops, and agents'
  },
  // [CASH-MODE]
  {
    title: 'Customer Cash QR',
    icon: faQrcode,
    path: '/customer-accounts/cash-qr',
    perm: 'customers.cashQr',
    description: 'View, print or regenerate the QR a customer shows at the shop when paying by cash'
  }
];

const CustomerAccounts = () => {
  const { can, loading } = usePermissions();
  const navigate = useNavigate();

  const handleCardClick = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <CircularProgress />
      </Grid>
    );
  }

  // Check if user has access to any of the cards
  const hasAnyAccess = customerAccountsOptions.some(option => can(option.perm));

  if (!hasAnyAccess) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <Typography variant="h6" color="error">
          You don't have permission to access customer account features. Please contact your administrator.
        </Typography>
      </Grid>
    );
  }

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />
      <br/><br/>
      <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center', mb: 4,mt:-7 }}>
        CUSTOMER MODULE
      </Typography>
      
      <Grid container spacing={3} justifyContent="center" alignItems="center" sx={{ padding: 2 }}>
        {customerAccountsOptions.map((option) => (
          can(option.perm) && (
            <Grid item xs={12} sm={6} md={4} lg={3} key={option.title}>
              <Card
                elevation={3}
                sx={{
                  maxWidth: 225,
                  height: '100%',
                  backgroundColor: 'white',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
                    '& .MuiTypography-root': { color: 'white' },
                    '& .fa-icon': { color: 'white' },
                  },
                }}
              >
                <CardActionArea 
                  onClick={() => handleCardClick(option.path)}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                >
                  <CardContent sx={{ textAlign: 'center', padding: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <FontAwesomeIcon
                      icon={option.icon}
                      size="5x"
                      className="fa-icon"
                      color="#FFA500"
                      style={{ marginBottom: '16px' }}
                    />
                    <Typography 
                      gutterBottom 
                      variant="h5" 
                      component="div" 
                      sx={{ fontWeight: 500 }}
                    >
                      {option.title}
                    </Typography>
                    <Typography 
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1, fontSize: '0.85rem' }}
                    >
                      {option.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          )
        ))}
      </Grid>
    </Box>
  );
};

export default CustomerAccounts;